/**
 * Main App Component
 *
 * Root component for the SPA.
 * Sets up React Router and renders the appropriate layout based on route.
 */

import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { routes } from './routes';
import { ChunkLoadErrorBoundary } from './components/ChunkLoadErrorBoundary';
import { useThemeStore } from '@/shared/store/themeStore';
import { useFirebaseStore } from '@/shared/store/firebaseStore';

// Create router instance - no basename needed, serving from root
const router = createBrowserRouter(routes);

export default function App() {
  // Mounted here rather than in AuthenticatedLayout so the OS listener also
  // covers /login and the pre-auth spinners — everything a signed-out visitor
  // can see. The theme class itself is already on <html> from the inline boot
  // script; this only takes over subsequent changes.
  const initializeTheme = useThemeStore((state) => state.initialize);
  useEffect(() => initializeTheme(), [initializeTheme]);

  // Cross-device sync. The user document arrives well after first paint (and
  // re-fires whenever another device changes the setting), so this reconciles
  // rather than initialises — localStorage already decided what was painted.
  const remoteTheme = useFirebaseStore((state) => state.currentUser?.uiPreferences?.theme);
  const adoptRemotePreference = useThemeStore((state) => state.adoptRemotePreference);
  useEffect(() => {
    if (remoteTheme) adoptRemotePreference(remoteTheme);
  }, [remoteTheme, adoptRemotePreference]);

  return (
    <ChunkLoadErrorBoundary>
      <RouterProvider router={router} />
    </ChunkLoadErrorBoundary>
  );
}